import React, { useState, useRef } from 'react';
import { Horse } from './types';
import { storeContractPDF } from './api';
import './ContractUploadDialog.css';

interface Props {
  horses: Horse[];
  onClose: () => void;
  onUploaded: (documentId: number) => void;
}

const ContractUploadDialog: React.FC<Props> = ({ horses, onClose, onUploaded }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedHorseId, setSelectedHorseId] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const visibleHorses = horses
    .filter(h => !h.hidden || h.contract)
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      alert('Bitte nur PDF-Dateien hochladen.');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Bitte nur PDF-Dateien hochladen.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const horse = horses.find(h => h.id === selectedHorseId);
      const contractId = horse?.contract?.id;

      const result = await storeContractPDF(
        selectedFile,
        selectedFile.name,
        contractId,
        selectedHorseId || undefined,
        'uploaded'
      );

      onUploaded(result.document_id);
      alert(`"${selectedFile.name}" erfolgreich hochgeladen.`);
      onClose();
    } catch (error: any) {
      console.error('Upload failed:', error);
      alert(`Upload fehlgeschlagen: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <>
      <div className="upload-overlay" onClick={onClose}></div>
      <div className="upload-dialog">
        <h3>Vertrag hochladen</h3>

        <div
          className={`drop-zone ${dragOver ? 'drop-zone-active' : ''} ${selectedFile ? 'drop-zone-has-file' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {selectedFile ? (
            <div className="file-info">
              <div className="file-icon">PDF</div>
              <div className="file-details">
                <div className="file-name">{selectedFile.name}</div>
                <div className="file-size">{formatFileSize(selectedFile.size)}</div>
              </div>
              <button
                className="btn-remove-file"
                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
              >
                X
              </button>
            </div>
          ) : (
            <div className="drop-hint">
              <div className="drop-icon">+</div>
              <p>PDF-Datei hierher ziehen oder klicken</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        <div className="upload-options">
          <div className="form-field">
            <label>Pferd zuordnen (optional)</label>
            <select
              value={selectedHorseId}
              onChange={(e) => setSelectedHorseId(e.target.value)}
            >
              <option value="">-- Später zuordnen --</option>
              {visibleHorses.map(h => (
                <option key={h.id} value={h.id}>
                  {h.name} {h.owner ? `(${h.owner})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="upload-actions">
          <button
            onClick={handleUpload}
            className="btn-upload"
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
          </button>
          <button onClick={onClose} className="btn-cancel">
            Abbrechen
          </button>
        </div>
      </div>
    </>
  );
};

export default ContractUploadDialog;
